import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { formatApiErrorDetail } from '../../utils/axios';
import axios from 'axios';
import {
  LayoutDashboard, Trophy, Users, Target, Ticket, Calendar,
  Menu, X, LogOut, FileText, Upload, Trash2, Award, Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

export const AdminSidebar = ({ active }) => {
  const navigate = useNavigate();
  const { admin, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/admin/dashboard' },
    { name: 'Pertandingan', icon: Calendar, path: '/admin/matches' },
    { name: 'Tim', icon: Trophy, path: '/admin/teams' },
    { name: 'Prediksi', icon: Target, path: '/admin/predictions' },
    { name: 'Tiket', icon: Ticket, path: '/admin/tickets' },
    { name: 'Partisipan', icon: Users, path: '/admin/participants' },
    { name: 'Partner / Website', icon: Users, path: '/admin/partners' },
    { name: 'Leaderboard', icon: Trophy, path: '/admin/leaderboard' },
    { name: 'Pemenang', icon: Award, path: '/admin/winners' },
    { name: 'Pengaturan Website', icon: FileText, path: '/admin/settings' },
  ];

  return (
    <>
      <aside className={`fixed inset-y-0 left-0 w-64 bg-[rgba(255,255,255,0.03)] border-r border-white/10 transform transition-transform lg:translate-x-0 z-50 overflow-y-auto ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D90429] to-[#B00320] rounded-xl flex items-center justify-center">
              <span className="text-white font-black text-2xl">ET</span>
            </div>
            <div>
              <div className="text-white font-black text-lg">EXTRA TIME</div>
              <div className="text-gray-400 text-xs">Admin Panel</div>
            </div>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  active === item.path
                    ? 'bg-[#D90429] text-white'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon size={18} />
                <span className="text-sm">{item.name}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6 border-t border-white/10 mt-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-[#D90429] rounded-full flex items-center justify-center">
              <span className="text-white font-bold">{admin?.username?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm truncate">{admin?.display_name || admin?.username}</div>
              <div className="text-gray-400 text-xs">{admin?.role}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-[60] p-2.5 rounded-lg bg-[#D90429] shadow-lg text-white"
        aria-label="Toggle menu"
        data-testid="admin-sidebar-toggle"
      >
        {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
      </button>
    </>
  );
};

const ExcelUploader = ({ endpoint, columns, sampleName, onSuccess, dataTestId }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error('File harus berformat .xlsx atau .xls');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    try {
      const { data } = await axios.post(`${API_BASE}${endpoint}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message || 'Import berhasil');
      if (data.errors && data.errors.length > 0) {
        toast.warning(`${data.errors.length} baris gagal diimport`);
      }
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Gagal upload');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="glass-card p-6" data-testid={dataTestId}>
      <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
        <Upload size={20} /> Upload Excel
      </h3>
      <p className="text-gray-400 text-sm mb-4">
        Format kolom yang diperlukan (baris pertama = header):
      </p>
      <div className="glass-card p-3 mb-4 bg-black/20">
        <code className="text-[#FFD60A] text-xs">
          {columns.join(' | ')}
        </code>
      </div>
      <p className="text-gray-500 text-xs mb-4">
        Contoh file: <span className="text-white">{sampleName}</span>. Upload akan mengganti semua data yang ada.
      </p>
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        onChange={handleUpload}
        className="hidden"
      />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="btn-primary w-full flex items-center justify-center gap-2"
        data-testid={`${dataTestId}-btn`}
      >
        <Upload size={18} />
        {uploading ? 'Uploading...' : 'Pilih File Excel'}
      </button>
    </div>
  );
};

export const AdminLeaderboard = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/leaderboard`, { withCredentials: true });
      setEntries(data);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleClear = async () => {
    if (!window.confirm('Yakin ingin menghapus semua data leaderboard?')) return;
    try {
      const { data } = await axios.delete(`${API_BASE}/admin/leaderboard/clear`, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error('Gagal menghapus');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-leaderboard-page">
      <AdminSidebar active="/admin/leaderboard" />

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Leaderboard</h1>
            <p className="text-gray-400">Upload data leaderboard dari file Excel</p>
          </div>
          {entries.length > 0 && (
            <button onClick={handleClear} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors">
              <Trash2 size={16} /> Hapus Semua
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-1">
            <ExcelUploader
              endpoint="/admin/leaderboard/import"
              columns={['Rank', 'Website', 'User ID', 'Skor Tepat', 'Pemenang Tepat', 'Total Poin', 'Hadiah']}
              sampleName="leaderboard.xlsx"
              onSuccess={fetchData}
              dataTestId="leaderboard-uploader"
            />
          </div>

          <div className="lg:col-span-2">
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold text-white mb-4">
                Data Leaderboard ({entries.length})
              </h3>
              {loading ? (
                <div className="text-gray-400">Loading...</div>
              ) : entries.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Belum ada data. Upload file Excel di sebelah kiri.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-white/10">
                      <tr className="text-gray-400 text-left">
                        <th className="py-2 pr-3">Rank</th>
                        <th className="py-2 pr-3">Website</th>
                        <th className="py-2 pr-3">User ID</th>
                        <th className="py-2">Hadiah</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {entries.map((e) => (
                        <tr key={e.id} className="text-white">
                          <td className="py-2 pr-3 font-bold text-[#FFD60A]">#{e.rank}</td>
                          <td className="py-2 pr-3">{e.website || '-'}</td>
                          <td className="py-2 pr-3 font-mono">{e.user_id}</td>
                          <td className="py-2 text-gray-300">{e.prize || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export const AdminWinners = () => {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/winners`, { withCredentials: true });
      setWinners(data);
    } catch (e) {
      setWinners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleClear = async () => {
    if (!window.confirm('Yakin ingin menghapus semua data pemenang?')) return;
    try {
      const { data } = await axios.delete(`${API_BASE}/admin/winners/clear`, { withCredentials: true });
      toast.success(data.message);
      fetchData();
    } catch (e) {
      toast.error('Gagal menghapus');
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1020]" data-testid="admin-winners-page">
      <AdminSidebar active="/admin/winners" />

      <main className="lg:ml-64 p-4 lg:p-8 pt-20 lg:pt-8">
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">Pemenang</h1>
            <p className="text-gray-400">Upload data pemenang dari file Excel</p>
          </div>
          {winners.length > 0 && (
            <button onClick={handleClear} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition-colors">
              <Trash2 size={16} /> Hapus Semua
            </button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-1">
            <ExcelUploader
              endpoint="/admin/winners/import"
              columns={['Rank', 'Website', 'User ID', 'Hadiah', 'Tanggal Menang', 'Deskripsi']}
              sampleName="winners.xlsx"
              onSuccess={fetchData}
              dataTestId="winners-uploader"
            />
          </div>

          <div className="lg:col-span-2">
            <div className="glass-card p-6">
              <h3 className="text-xl font-bold text-white mb-4">
                Data Pemenang ({winners.length})
              </h3>
              {loading ? (
                <div className="text-gray-400">Loading...</div>
              ) : winners.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Belum ada data. Upload file Excel di sebelah kiri.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {winners.map((w) => (
                    <div key={w.id} className="glass-card p-4 bg-white/5">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-bold text-white">{w.user_id}</div>
                        {w.rank && <div className="text-[#FFD60A] font-bold text-sm">#{w.rank}</div>}
                      </div>
                      {w.website && <div className="text-gray-400 text-xs mb-2">{w.website}</div>}
                      {w.prize && <div className="text-[#22C55E] font-semibold text-sm mb-1">🏆 {w.prize}</div>}
                      {w.winning_date && (
                        <div className="text-gray-500 text-xs">
                          {new Date(w.winning_date).toLocaleDateString('id-ID')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};
